import { Button, Col, Row, Sidepanel, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { GOALRUSH_SESSION_STATUS } from '../constants';
import { Badge, PanelHeader } from '../../FanWave/components/ui';
import { fmtLength, fmtSpan, fmtTime, statusOf } from './sessionFormat';

const num = (n) => (n ?? 0).toLocaleString();
const secs = (ms) => `${(ms ?? 0) / 1000}s`;

// A titled card inside the panel body (the FanWave detail panel's Block).
const Block = ({ title, right, children }) => (
  <Col
    noFlex
    wid="100%"
    pad="16px 18px"
    gap="10px"
    style={{
      background: '#fff',
      borderRadius: '14px',
      border: `1px solid ${theme.GREY_20}`,
      boxShadow: '0 1px 2px rgba(20, 28, 40, 0.04)'
    }}
  >
    <Row center between noFlex wid="100%">
      <Text h4 bold marg="0">
        {title}
      </Text>
      {right}
    </Row>
    {children}
  </Col>
);

const Line = ({ label, value }) => (
  <Row
    center
    between
    noFlex
    wid="100%"
    pad="5px 0"
    gap="12px"
    style={{ borderTop: `1px solid ${theme.GREY_10}` }}
  >
    <Text b2 col={theme.GREY_60} marg="0" style={{ fontSize: '12px' }}>
      {label}
    </Text>
    <Text b2 bold marg="0" style={{ fontSize: '12.5px', textAlign: 'right' }}>
      {value}
    </Text>
  </Row>
);

// Used only by the hidden Gameplay block. Uncomment with it.
// const StatCard = ({ label, value }) => (
//   <Col
//     noFlex
//     pad="10px 12px"
//     style={{
//       flex: '1 1 110px',
//       minWidth: '100px',
//       background: theme.GREY_10,
//       borderRadius: '11px',
//       border: `1px solid ${theme.GREY_20}`
//     }}
//   >
//     <Text
//       marg="0"
//       style={{
//         fontSize: '10px',
//         fontWeight: 700,
//         letterSpacing: '0.05em',
//         textTransform: 'uppercase',
//         color: theme.GREY_50
//       }}
//     >
//       {label}
//     </Text>
//     <Text marg="4px 0 0" style={{ fontSize: '18px', fontWeight: 800, lineHeight: 1.1 }}>
//       {value}
//     </Text>
//   </Col>
// );

// "1,234 / 5,000" over a bar filled to the share of the target.
const Progress = ({ label, value, target }) => {
  const pct = target ? Math.min(100, (value / target) * 100) : 0;
  return (
    <Col noFlex wid="100%" gap="6px">
      <Row center between noFlex wid="100%">
        <Text b2 col={theme.GREY_60} marg="0" style={{ fontSize: '12px' }}>
          {label}
        </Text>
        <Text b2 bold marg="0" style={{ fontSize: '12.5px' }}>
          {num(value)} / {num(target)}
        </Text>
      </Row>
      <div style={{ height: 6, borderRadius: 3, background: theme.GREY_10, overflow: 'hidden' }}>
        <div
          style={{
            width: `${Math.max(pct, value ? 2 : 0)}%`,
            height: '100%',
            borderRadius: 3,
            background: pct >= 100 ? theme.GREEN : theme.SECONDARY
          }}
        />
      </div>
    </Col>
  );
};

const Avatar = ({ user }) =>
  user?.avatar ? (
    <img
      src={user.avatar}
      alt=""
      style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
    />
  ) : (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        flexShrink: 0,
        background: theme.GREY_10,
        color: theme.GREY_60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 700
      }}
    >
      {(user?.name || user?.username || '?').charAt(0).toUpperCase()}
    </div>
  );

const FanCol = ({ children, wid = '64px', head }) => (
  <Text
    b2
    bold={!head}
    col={head ? theme.GREY_50 : undefined}
    marg="0"
    style={{ width: wid, flexShrink: 0, textAlign: 'right', fontSize: head ? '11px' : '12.5px' }}
  >
    {children}
  </Text>
);

/**
 * One GoalRush session's details, opened from a Sessions row: progress against its targets,
 * gameplay totals, pauses, the rules it ran with, its top fans and settlement. The row's summary
 * fills the header at once; the rest loads from GET /admin/goalrush/session.
 */
const SessionDrawer = ({ visible, teamId, session, onClose }) => {
  const { t } = useTranslation();
  const k = (key, opts) => t(`admin.goalRush.sessions.detail.${key}`, opts);
  const dispatch = useDispatch();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const sessionId = session?.id;

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    const res = await Api.get(
      `/admin/goalrush/session?teamId=${teamId}&sessionId=${sessionId}`,
      null,
      { silent: true }
    );
    setLoading(false);
    if (res?.data) setDetail(res.data);
    else if (res?.err) dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
  }, [teamId, sessionId, dispatch]);

  // A different session never shows the last one's details while its own load.
  useEffect(() => {
    setDetail(null);
  }, [sessionId]);

  useEffect(() => {
    if (visible) load();
  }, [visible, load]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  // Guarded on `sessionId`: with no row opened yet both ids are undefined and would match.
  const s = sessionId && detail?.session?.id === sessionId ? detail.session : null;
  const head = s || session;
  const status = head ? statusOf(head) : null;
  const ended = head?.status === GOALRUSH_SESSION_STATUS.ENDED;

  let when = head ? fmtSpan(head.startedAt, head.endedAt ?? head.endsAt) : '';
  if (head?.endedAt) {
    when += ` (${fmtLength(new Date(head.endedAt) - new Date(head.startedAt))})`;
  } else if (head && !head.endsAt) {
    when += ` · ${t('admin.goalRush.sessions.noLimit')}`;
  }

  const rules = s?.rules;
  const others = rules?.others;
  const fans = s ? detail.fans : [];

  return (
    <Sidepanel visible={visible} wid={640} pad="0" closeModal={onClose}>
      <PanelHeader text={k('title')} onClose={onClose} />
      {head && (
        <Col wid="100%" pad="18px 24px 40px" gap="14px">
          {/* Header */}
          <Row center between noFlex wid="100%" gap="10px" style={{ flexWrap: 'wrap' }}>
            <Col noFlex gap="6px">
              <Row center noFlex gap="8px">
                <Text b2 bold marg="0" style={{ fontSize: '15px' }}>
                  {t(`admin.goalRush.sessions.kind.${head.kind}`)}
                </Text>
                <Badge variant={status.variant} dot>
                  {t(`admin.goalRush.sessions.status.${status.key}`)}
                </Badge>
              </Row>
              <Text c1 col={theme.GREY_70} marg="0" style={{ fontSize: '12.5px' }}>
                {when}
              </Text>
              {head.status === GOALRUSH_SESSION_STATUS.PAUSED && (
                <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12px' }}>
                  {t('admin.goalRush.sessions.pausedSince', { time: fmtTime(head.pausedAt) })}
                </Text>
              )}
            </Col>
            <Button
              textButton
              small
              text={t('admin.goalRush.sessions.refresh')}
              onClick={load}
              loading={loading}
            />
          </Row>

          {/* Progress */}
          <Block title={k('progress')}>
            <Progress label={k('kicks')} value={head.counters.kicks} target={head.targets.kicks} />
            <Progress label={k('goals')} value={head.counters.goals} target={head.targets.goals} />
            <Line label={t('admin.goalRush.sessions.fans')} value={num(head.participantCount)} />
            {ended && (
              <Line
                label={k('endReason')}
                value={k(`endReasons.${head.endReason}`, { defaultValue: head.endReason })}
              />
            )}
            {s?.targetHits.map((hit) => (
              <Line
                key={hit.kind}
                label={k(`targetHit.${hit.kind}`, { time: fmtTime(hit.reachedAt) })}
                value={k('rewardPerFan', { value: num(hit.rewardPoints) })}
              />
            ))}
          </Block>

          {!s ? (
            <Text c1 col={theme.GREY_60} marg="0">
              {t('admin.goalRush.loading')}
            </Text>
          ) : (
            <>
              {/* Gameplay — hidden. Uncomment (and StatCard above) to switch back.
              <Block title={k('gameplay')}>
                <Row noFlex wid="100%" gap="8px" style={{ flexWrap: 'wrap' }}>
                  {['blocked', 'nearMiss', 'collisions', 'rebounds', 'bonusHits'].map((key) => (
                    <StatCard key={key} label={k(`stats.${key}`)} value={num(s.gameplay[key])} />
                  ))}
                </Row>
              </Block>
              */}

              {/* Pauses */}
              {s.pauses.length > 0 && (
                <Block title={k('pauses')}>
                  {s.pauses.map((p) => (
                    <Line
                      key={p.from}
                      label={fmtSpan(p.from, p.to)}
                      value={
                        p.to ? fmtLength(new Date(p.to) - new Date(p.from)) : k('pauseOngoing')
                      }
                    />
                  ))}
                </Block>
              )}

              {/* Rules used */}
              <Block title={k('rules')}>
                <Line
                  label={t('admin.goalRush.fields.sessionDurationSec')}
                  value={
                    rules.sessionDurationSec
                      ? fmtLength(rules.sessionDurationSec * 1000)
                      : t('admin.goalRush.sessions.noLimit')
                  }
                />
                <Line
                  label={t('admin.goalRush.fields.targetKicks')}
                  value={num(rules.targetKicks)}
                />
                <Line
                  label={t('admin.goalRush.fields.targetGoals')}
                  value={num(rules.targetGoals)}
                />
                <Line
                  label={t('admin.goalRush.fields.kickMilestonePoints')}
                  value={num(rules.kickMilestonePoints)}
                />
                <Line
                  label={t('admin.goalRush.fields.goalMilestonePoints')}
                  value={num(rules.goalMilestonePoints)}
                />
                <Line
                  label={t('admin.goalRush.fields.pointsPerKick')}
                  value={num(rules.pointsPerKick)}
                />
                <Line
                  label={t('admin.goalRush.fields.pointsPerGoal')}
                  value={num(rules.pointsPerGoal)}
                />
                <Line
                  label={t('admin.goalRush.fields.pointsPerBonusHit')}
                  value={num(rules.pointsPerBonusHit)}
                />
                {/* Hidden. Uncomment to switch back.
                <Line label={k('kickCooldown')} value={secs(rules.kickCooldownMs)} />
                */}
                <Line
                  label={t('admin.goalRush.fields.defenderPatternId')}
                  value={t(`admin.goalRush.patterns.${rules.defenderPatternId}.label`, {
                    defaultValue: rules.defenderPatternId
                  })}
                />
                <Line
                  label={t('admin.goalRush.rows.gaps')}
                  value={
                    others?.gapsEnabled
                      ? k('gapsSummary', {
                          every: others.gapIntervalSec,
                          open: secs(others.gapDurationMs),
                          width: others.minGapWidth
                        })
                      : k('off')
                  }
                />
                <Line
                  label={t('admin.goalRush.rows.bonus')}
                  value={
                    others?.bonusEnabled
                      ? k('bonusSummary', {
                          every: others.bonusIntervalSec,
                          visible: secs(others.bonusVisibleMs)
                        })
                      : k('off')
                  }
                />
              </Block>

              {/* Fans */}
              <Block
                title={k('fans')}
                right={
                  detail.fansTotal > fans.length && (
                    <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12px' }}>
                      {k('fansTop', { shown: num(fans.length), total: num(detail.fansTotal) })}
                    </Text>
                  )
                }
              >
                {!fans.length ? (
                  <Text c1 col={theme.GREY_60} marg="0">
                    {k('fansNone')}
                  </Text>
                ) : (
                  <Col noFlex wid="100%">
                    <Row center noFlex wid="100%" gap="10px" pad="0 0 6px">
                      <Text b2 col={theme.GREY_50} marg="0" style={{ flex: 1, fontSize: '11px' }}>
                        {k('fan')}
                      </Text>
                      <FanCol head>{k('kicks')}</FanCol>
                      <FanCol head>{k('goals')}</FanCol>
                      <FanCol head wid="72px">
                        {k('points')}
                      </FanCol>
                    </Row>
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {fans.map((f, i) => (
                        <Row
                          key={f.user?._id || i}
                          center
                          noFlex
                          wid="100%"
                          gap="10px"
                          pad="7px 0"
                          style={{ borderTop: `1px solid ${theme.GREY_10}` }}
                        >
                          <Row center noFlex gap="8px" style={{ flex: 1, minWidth: 0 }}>
                            <Avatar user={f.user} />
                            <Text
                              b2
                              marg="0"
                              style={{
                                fontSize: '12.5px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {f.user?.name || f.user?.username || k('deletedFan')}
                            </Text>
                          </Row>
                          <FanCol>{num(f.kicks)}</FanCol>
                          <FanCol>{num(f.goals)}</FanCol>
                          <FanCol wid="72px">{num(f.points + f.milestonePoints)}</FanCol>
                        </Row>
                      ))}
                    </div>
                  </Col>
                )}
              </Block>

              {/* Settlement — hidden. Uncomment to switch back.
              <Block title={k('settlement')}>
                <Text c1 col={theme.GREY_70} marg="0" style={{ fontSize: '12.5px' }}>
                  {s.settled
                    ? k('settled', { time: fmtTime(s.settledAt) })
                    : ended
                      ? k('settlePending')
                      : k('settleOnEnd')}
                </Text>
              </Block>
              */}
            </>
          )}
        </Col>
      )}
    </Sidepanel>
  );
};

export default SessionDrawer;
